/**
 * CollectingFrame: third frame of Add Building, between Locate and the
 * post-POST close. Warms snapshots for the building just added.
 *
 * UX Option A (CTO spec):
 *   - 8s floor. Never advance earlier; below 8s the UI flashes.
 *   - Auto-advance fires EXACTLY ONCE on identity.ready AND exposure.ready
 *     AND elapsed >= 8000. `inspections` keeps draining in the background.
 *   - "Skip ahead" appears only after 3s: an escape hatch, not a default.
 *   - 90s timeout does NOT auto-advance; the user clicks the CTA. A worker
 *     wedge (Celery dead, Redis unreachable) is the expected path, so that
 *     CTA is correct, not a bug.
 *
 * 3 tiers, NOT 14 rows. No new color or animation tokens
 * (`feedback_tailwind_tokens_via_cssvars_only`).
 */
import * as React from "react";

import { cn } from "@/lib/utils";
import {
  useWarmupStatusQuery,
  type WarmupGroup,
  type WarmupGroupStatus,
} from "@/queries/portalQueries";

/** Minimum time the frame stays visible. */
const FLOOR_MS = 8_000;
/** When "Skip ahead" unlocks. */
const SKIP_AFTER_MS = 3_000;
/** Auto-advance stops; the manual proceed CTA takes over. */
const TIMEOUT_MS = 90_000;
/** Elapsed-counter re-render cadence, no query refetch. 250ms reads as
 * live without churning renders. */
const TICK_MS = 250;

/**
 * Re-renders every TICK_MS so the elapsed-time gates read a fresh clock.
 * NOT useState({elapsed}): that couples elapsed to React's batching and
 * misses the 8s threshold by a frame on a slow machine.
 */
function useElapsed(startedAtMs: number): number {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = window.setInterval(() => {
      setTick((n) => (n + 1) % 1_000_000);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, []);
  return Date.now() - startedAtMs;
}

export interface CollectingFrameProps {
  /** UUID from the Add Building POST; drives the warmup-status query. */
  buildingId: string;
  /** Fired exactly once when the gate passes. */
  onAdvance: () => void;
  /** The frame renders no cancel button today; kept on the contract so a
   * parent can wire one. */
  onCancel?: () => void;
  /** Stable test selector. */
  testId?: string;
}

interface GroupRowDescriptor {
  id: "identity" | "exposure" | "inspections";
  label: string;
  /** What this group covers. */
  helper: string;
}

const GROUP_ROWS: readonly GroupRowDescriptor[] = [
  {
    id: "identity",
    label: "Identity",
    helper: "PLUTO and Building Footprints",
  },
  {
    id: "exposure",
    label: "Penalty exposure",
    helper: "ECB, LL84, HPD Reg, DOB permits, complaints, violations",
  },
  {
    id: "inspections",
    label: "Inspections",
    helper: "Boiler, elevator, FISP, cooling tower, bedbug, LL152, FDNY",
  },
];

/**
 * The BE `status` is authoritative, but `data` is undefined for the first
 * ~2s before the first poll lands. That reads as `pending` so the row draws
 * a half-disc, not a blank slot.
 */
function statusFor(group: WarmupGroup | undefined): WarmupGroupStatus {
  if (!group) return "pending";
  return group.status;
}

/** Presentational glyph. */
const StatusGlyph: React.FC<{
  status: WarmupGroupStatus;
  testId?: string;
}> = ({ status, testId }) => {
  if (status === "ready") {
    return (
      <span
        data-testid={testId}
        data-status="ready"
        aria-hidden="true"
        className="inline-flex h-3 w-3 items-center justify-center rounded-full bg-ink text-paper"
      >
        <svg
          viewBox="0 0 8 8"
          className="h-2 w-2"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M1.5 4.25 L3.25 6 L6.5 2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  if (status === "error") {
    // Single dash: the convention elsewhere for an unreachable source.
    return (
      <span
        data-testid={testId}
        data-status="error"
        aria-hidden="true"
        className="inline-flex h-3 w-3 items-center justify-center rounded-full border border-ink-line-strong text-ink-mute"
      >
        <span className="block h-px w-1.5 bg-current" />
      </span>
    );
  }
  // pending: half-disc plus SyncIndicator's own pulse.
  return (
    <span
      data-testid={testId}
      data-status="pending"
      aria-hidden="true"
      className="relative inline-flex h-3 w-3 items-center justify-center rounded-full border border-ink-line-strong"
    >
      <span className="absolute inset-0.5 rounded-full bg-ink animate-cc-pulse" />
    </span>
  );
};

const CollectingFrame: React.FC<CollectingFrameProps> = ({
  buildingId,
  onAdvance,
  onCancel: _onCancel,
  testId = "collecting-frame",
}) => {
  // `useRef`, not `useState`: setting mount time must not re-render.
  const startedAtRef = React.useRef<number>(Date.now());
  const elapsed = useElapsed(startedAtRef.current);

  // Fires onAdvance EXACTLY once. State here would re-render and race
  // the gate.
  const advancedRef = React.useRef(false);

  const { data, isError } = useWarmupStatusQuery(buildingId, true);

  const identity = data?.groups.identity;
  const exposure = data?.groups.exposure;
  const inspections = data?.groups.inspections;

  const identityReady = Boolean(identity?.ready);
  const exposureReady = Boolean(exposure?.ready);
  const inspectionsStatus = statusFor(inspections);

  const anyError =
    statusFor(identity) === "error" ||
    statusFor(exposure) === "error" ||
    inspectionsStatus === "error";

  const gateOpen = identityReady && exposureReady && elapsed >= FLOOR_MS;
  const skipUnlocked = elapsed >= SKIP_AFTER_MS;
  const timedOut = elapsed >= TIMEOUT_MS;

  React.useEffect(() => {
    if (advancedRef.current) return;
    if (gateOpen) {
      advancedRef.current = true;
      onAdvance();
    }
  }, [gateOpen, onAdvance]);

  const handleSkip = React.useCallback(() => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    onAdvance();
  }, [onAdvance]);

  // The headline does not change on error; the error affordance is in the
  // group rows below.
  return (
    <div
      data-testid={testId}
      data-state={
        timedOut
          ? "timed-out"
          : gateOpen
            ? "ready"
            : anyError
              ? "partial-error"
              : "syncing"
      }
      className="space-y-6"
      role="status"
      aria-live="polite"
    >
      <header className="space-y-2">
        <h2
          className="font-display text-2xl font-medium text-ink leading-tight"
          data-testid={`${testId}-headline`}
        >
          Collecting building records
        </h2>
        <p className="text-sm text-ink-mute">
          Pulling identity, exposure, and inspection records from NYC city
          sources. Usually takes 1&ndash;2 minutes.
        </p>
      </header>

      <ul className="space-y-4" data-testid={`${testId}-groups`}>
        {GROUP_ROWS.map((row) => {
          const group =
            row.id === "identity"
              ? identity
              : row.id === "exposure"
                ? exposure
                : inspections;
          const status = statusFor(group);
          const freshCount = group?.freshCount ?? 0;
          const totalCount = group?.totalCount ?? 0;
          return (
            <li
              key={row.id}
              data-testid={`${testId}-group-${row.id}`}
              data-status={status}
              className="flex items-start gap-3"
            >
              <span className="mt-1 shrink-0">
                <StatusGlyph
                  status={status}
                  testId={`${testId}-glyph-${row.id}`}
                />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-ink">
                    {row.label}
                  </span>
                  <span
                    className="font-mono text-3xs uppercase tracking-[0.05em] text-ink-mute"
                    data-testid={`${testId}-count-${row.id}`}
                  >
                    {totalCount > 0
                      ? `${freshCount} of ${totalCount} sources`
                      : "loading…"}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-ink-mute">{row.helper}</p>
                {status === "error" && (
                  <button
                    type="button"
                    onClick={handleSkip}
                    data-testid={`${testId}-continue-anyway-${row.id}`}
                    className={cn(
                      "mt-1 text-xs text-ink-body underline-offset-2 hover:underline",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-line-strong",
                    )}
                  >
                    Continue anyway?
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Skip ahead (after 3s) OR the timed-out CTA, never both: the
          timed-out CTA is the stronger statement and takes the slot. */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <span
          className="font-mono text-3xs uppercase tracking-[0.05em] text-ink-faint"
          data-testid={`${testId}-elapsed`}
        >
          {Math.floor(elapsed / 1000)}s elapsed
        </span>
        {timedOut ? (
          <button
            type="button"
            onClick={handleSkip}
            data-testid={`${testId}-proceed-anyway`}
            className="rounded-md border border-ink-line px-3 py-1.5 text-xs font-medium text-ink hover:bg-ink-line/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-line-strong"
          >
            Some sources didn&rsquo;t respond &mdash; proceed anyway?
          </button>
        ) : skipUnlocked ? (
          <button
            type="button"
            onClick={handleSkip}
            data-testid={`${testId}-skip-ahead`}
            className="text-xs font-medium text-ink-body underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-line-strong"
          >
            Skip ahead
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
      </div>

      {/* Whole-query failure (network down, BE 5xx): say so rather than
          freezing for 90s. Polling continues; a successful tick opens the
          gate normally. */}
      {isError && !timedOut && (
        <p
          data-testid={`${testId}-query-error`}
          className="text-xs text-ink-mute"
        >
          We couldn&rsquo;t reach the warmup service. Retrying&hellip;
        </p>
      )}
    </div>
  );
};

export default CollectingFrame;
